/**
 * ECSR-1188 — Mini cart scroll lock
 *
 * While the mini cart (#Cart-Drawer.active) is open, wheel, trackpad and touch
 * scrolling stay inside it: the page behind never moves, the page scrollbar
 * stays where it was, and the page is at the same position when the cart
 * closes.
 *
 * Why not `overflow: hidden` / `position: fixed` on <body>: iOS Safari ignores
 * the former for touch, and the latter jumps the page scrollbar to the top
 * while the cart is open. Instead this:
 *   - cancels any wheel / touchmove gesture that no scrollable element under
 *     the pointer can absorb in that direction (so it would otherwise chain to
 *     the page), including on the cart header, the Total/checkout area and a
 *     short cart that does not scroll at all;
 *   - lets every scrollable area (the cart list, the order-note textarea,
 *     any app popup) keep scrolling normally until its own end;
 *   - as a backstop, puts the page back to the opening position if anything
 *     else (keyboard, scrollbar drag) moves it while the cart is open.
 *
 * Works with every code path that opens/closes the drawer because it watches
 * the drawer's `active` class instead of hooking individual buttons.
 */
(function () {
  'use strict';

  var DRAWER_ID = 'Cart-Drawer';
  var drawer = document.getElementById(DRAWER_ID);
  if (!drawer || typeof MutationObserver === 'undefined') return;

  var locked = false;
  var lockX = 0;
  var lockY = 0;
  var touchX = 0;
  var touchY = 0;
  var restoring = false;

  var listenerOpts = { passive: false, capture: true };

  function isScrollable(el, axis) {
    var style = window.getComputedStyle(el);
    var overflow = axis === 'y' ? style.overflowY : style.overflowX;
    if (overflow !== 'auto' && overflow !== 'scroll' && overflow !== 'overlay') {
      return false;
    }
    return axis === 'y'
      ? el.scrollHeight - el.clientHeight > 1
      : el.scrollWidth - el.clientWidth > 1;
  }

  function canScroll(el, axis, delta) {
    if (axis === 'y') {
      if (delta < 0) return el.scrollTop > 0;
      return Math.ceil(el.scrollTop + el.clientHeight) < el.scrollHeight;
    }
    if (delta < 0) return el.scrollLeft > 0;
    return Math.ceil(el.scrollLeft + el.clientWidth) < el.scrollWidth;
  }

  // True when some element between the target and the page can take this
  // scroll; false when it would fall through to the page.
  function absorbedBelowPage(target, axis, delta) {
    var el = target && target.nodeType === 1 ? target : target && target.parentElement;
    while (el && el !== document.body && el !== document.documentElement) {
      if (isScrollable(el, axis) && canScroll(el, axis, delta)) return true;
      el = el.parentElement;
    }
    return false;
  }

  // True when the target sits inside a vertically scrollable element within
  // the cart drawer (the cart list, the order-note textarea, an app popup).
  function insideVerticalScroller(target) {
    var el = target && target.nodeType === 1 ? target : target && target.parentElement;
    if (!el || !drawer.contains(el)) return false;
    while (el && el !== document.body && el !== document.documentElement) {
      if (isScrollable(el, 'y')) return true;
      if (el === drawer) break;
      el = el.parentElement;
    }
    return false;
  }

  function onWheel(event) {
    if (!locked || event.ctrlKey) return; // ctrl+wheel is pinch-zoom on trackpads
    var dx = event.deltaX;
    var dy = event.deltaY;
    if (event.shiftKey && !dx) {
      dx = dy;
      dy = 0;
    }
    var axis = Math.abs(dy) >= Math.abs(dx) ? 'y' : 'x';
    var delta = axis === 'y' ? dy : dx;
    if (!delta) return;
    if (!absorbedBelowPage(event.target, axis, delta) && event.cancelable) {
      event.preventDefault();
    }
  }

  function onTouchStart(event) {
    if (!locked || event.touches.length !== 1) return;
    touchX = event.touches[0].clientX;
    touchY = event.touches[0].clientY;
  }

  function onTouchMove(event) {
    if (!locked || event.touches.length !== 1) return; // leave pinch-zoom alone
    var x = event.touches[0].clientX;
    var y = event.touches[0].clientY;
    // Finger moving up scrolls content down (positive delta), like a wheel.
    var dx = touchX - x;
    var dy = touchY - y;
    touchX = x;
    touchY = y;
    // Touch browsers decide whether to start a native pan from the first
    // touchmove, so every sample is policed on its dominant axis (like the
    // wheel): one that is sideways or has no movement must still be cancelled
    // unless something inside the cart can take it, or the page starts to pan
    // and later touchmoves can no longer be cancelled. Real horizontal
    // scrollers inside the cart still absorb sideways swipes.
    //
    // Exception: inside a vertical scroller in the cart (the cart list), a
    // sideways or zero-movement sample is left alone so a curved swipe can
    // still start the list's own scroll; the list's
    // `overscroll-behavior: contain` keeps that gesture off the page. The
    // header and the Total/checkout area are not inside a scroller, so they
    // stay fully cancelled.
    var axis = Math.abs(dy) >= Math.abs(dx) ? 'y' : 'x';
    var delta = axis === 'y' ? dy : dx;
    if (!delta) {
      if (!insideVerticalScroller(event.target) && event.cancelable) {
        event.preventDefault();
      }
      return;
    }
    if (absorbedBelowPage(event.target, axis, delta)) return;
    if (axis === 'x' && insideVerticalScroller(event.target)) return;
    if (event.cancelable) event.preventDefault();
  }

  function restorePosition() {
    if (restoring) return;
    var x = window.pageXOffset;
    var y = window.pageYOffset;
    if (x === lockX && y === lockY) return;
    restoring = true;
    // The theme sets `scroll-behavior: smooth` on <html>; restore instantly.
    var previous = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    window.scrollTo(lockX, lockY);
    document.documentElement.style.scrollBehavior = previous;
    restoring = false;
  }

  function onScroll() {
    if (locked) restorePosition();
  }

  function lock() {
    if (locked) return;
    locked = true;
    lockX = window.pageXOffset;
    lockY = window.pageYOffset;
    document.addEventListener('wheel', onWheel, listenerOpts);
    document.addEventListener('touchstart', onTouchStart, listenerOpts);
    document.addEventListener('touchmove', onTouchMove, listenerOpts);
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function unlock() {
    if (!locked) return;
    restorePosition();
    locked = false;
    document.removeEventListener('wheel', onWheel, listenerOpts);
    document.removeEventListener('touchstart', onTouchStart, listenerOpts);
    document.removeEventListener('touchmove', onTouchMove, listenerOpts);
    window.removeEventListener('scroll', onScroll, { passive: true });
  }

  function sync() {
    if (drawer.classList.contains('active')) {
      lock();
    } else {
      unlock();
    }
  }

  new MutationObserver(sync).observe(drawer, {
    attributes: true,
    attributeFilter: ['class']
  });
  sync();
})();
